import { NO_MEMBER_GRANTS, type OrgMember } from "../../orgMembers";

type ClerkPublicUser = {
  readonly firstName?: string | null;
  readonly lastName?: string | null;
  readonly identifier?: string | null;
  readonly userId?: string | null;
};

type ClerkMembershipEntry = {
  readonly publicUserData?: ClerkPublicUser | null;
  readonly role?: string | null;
};

type ClerkInvitationEntry = {
  readonly emailAddress?: string | null;
  readonly role?: string | null;
  readonly status?: string | null;
};

function clerkMemberDisplayName(publicUser: ClerkPublicUser | null | undefined): string {
  const fullName = [publicUser?.firstName, publicUser?.lastName].filter(Boolean).join(" ").trim();
  return fullName || publicUser?.identifier?.trim() || "";
}

function normalizeEmail(email: string | null | undefined): string {
  return email?.trim().toLowerCase() ?? "";
}

function orgMemberFromDb(db: OrgMember, overrides: Partial<OrgMember> = {}): OrgMember {
  return {
    email: db.email,
    name: db.name,
    userId: db.userId,
    clerkRole: db.clerkRole,
    title: db.title,
    functionRole: db.functionRole,
    description: db.description,
    avatarSeed: db.avatarSeed,
    defaultSensitivity: db.defaultSensitivity,
    invitedBy: db.invitedBy,
    joined: db.joined,
    grants: db.grants,
    createdAt: db.createdAt,
    updatedAt: db.updatedAt,
    ...overrides,
  };
}

function orgMemberFromClerkMembership(
  entry: ClerkMembershipEntry,
  db: OrgMember | undefined,
): OrgMember | null {
  const publicUser = entry.publicUserData;
  const userId = publicUser?.userId?.trim() ?? "";
  const email = normalizeEmail(publicUser?.identifier);
  if (!userId || !email) return null;
  const name = clerkMemberDisplayName(publicUser);
  return {
    email,
    name: db?.name?.trim() || name || email,
    userId,
    clerkRole: db?.clerkRole || entry.role?.trim() || "org:member",
    title: db?.title ?? "",
    functionRole: db?.functionRole ?? "",
    description: db?.description ?? "",
    avatarSeed: db?.avatarSeed ?? "",
    defaultSensitivity: db?.defaultSensitivity ?? "normal",
    invitedBy: db?.invitedBy ?? null,
    joined: true,
    grants: db?.grants ?? NO_MEMBER_GRANTS,
    createdAt: db?.createdAt ?? "",
    updatedAt: db?.updatedAt ?? "",
  };
}

function orgMemberFromClerkInvitation(
  invitation: ClerkInvitationEntry,
  db: OrgMember | undefined,
): OrgMember | null {
  const email = normalizeEmail(invitation.emailAddress);
  if (!email) return null;
  return {
    email,
    name: db?.name?.trim() || email,
    userId: db?.userId ?? null,
    clerkRole: db?.clerkRole || invitation.role?.trim() || "org:member",
    title: db?.title ?? "",
    functionRole: db?.functionRole ?? "",
    description: db?.description ?? "",
    avatarSeed: db?.avatarSeed ?? "",
    defaultSensitivity: db?.defaultSensitivity ?? "normal",
    invitedBy: db?.invitedBy ?? null,
    joined: false,
    grants: db?.grants ?? NO_MEMBER_GRANTS,
    createdAt: db?.createdAt ?? "",
    updatedAt: db?.updatedAt ?? "",
  };
}

/**
 * Merge Clerk's authoritative membership/invite roster with our DB-backed rich
 * fields. Clerk is the source of truth for who has joined; the DB may lag
 * (webhook delay, invite created only in Clerk, or a non-admin 403 on fetch).
 */
export function mergeTeamOrgDirectory(input: {
  readonly dbMembers: ReadonlyArray<OrgMember>;
  readonly clerkMemberships: ReadonlyArray<ClerkMembershipEntry> | undefined;
  readonly clerkInvitations: ReadonlyArray<ClerkInvitationEntry> | undefined;
}): { readonly joinedMembers: OrgMember[]; readonly invitedMembers: OrgMember[] } {
  const dbByEmail = new Map(
    input.dbMembers.map((member) => [normalizeEmail(member.email), member] as const),
  );
  const joinedMembers: OrgMember[] = [];
  const joinedEmails = new Set<string>();

  for (const entry of input.clerkMemberships ?? []) {
    const email = normalizeEmail(entry.publicUserData?.identifier);
    const member = orgMemberFromClerkMembership(entry, dbByEmail.get(email));
    if (!member) continue;
    joinedMembers.push(member);
    joinedEmails.add(member.email);
  }

  for (const db of input.dbMembers) {
    const email = normalizeEmail(db.email);
    if (!db.joined || joinedEmails.has(email)) continue;
    joinedMembers.push(orgMemberFromDb(db, { joined: true }));
    joinedEmails.add(email);
  }

  const invitedMembers: OrgMember[] = [];
  const invitedEmails = new Set<string>();

  for (const invitation of input.clerkInvitations ?? []) {
    if (invitation.status !== "pending") continue;
    const email = normalizeEmail(invitation.emailAddress);
    if (!email || joinedEmails.has(email) || invitedEmails.has(email)) continue;
    const member = orgMemberFromClerkInvitation(invitation, dbByEmail.get(email));
    if (!member) continue;
    invitedMembers.push(member);
    invitedEmails.add(email);
  }

  for (const db of input.dbMembers) {
    const email = normalizeEmail(db.email);
    if (db.joined || joinedEmails.has(email) || invitedEmails.has(email)) continue;
    invitedMembers.push(db);
    invitedEmails.add(email);
  }

  return { joinedMembers, invitedMembers };
}

export function isWorkspaceTeamAdmin(input: {
  readonly orgLoaded?: boolean;
  /**
   * Whether Clerk (multi-user auth) is configured. When it isn't, the app runs
   * as a single-user local/personal workspace and that sole user is the admin.
   */
  readonly clerkEnabled?: boolean;
  readonly organizationId: string | null;
  readonly orgRole: string | null | undefined;
}): boolean {
  if (input.orgLoaded === false) return false;
  // An active organization: admin is strictly the Clerk `org:admin` role. This is
  // cosmetic only — the server's `requireOrgPermission` gate is the real boundary.
  if (input.organizationId) return input.orgRole === "org:admin";
  // No active organization. Only the genuinely local / no-Clerk personal
  // workspace makes the sole user an admin. When Clerk IS enabled but no org is
  // active (a personal Clerk account, or a membership still pending), never
  // auto-grant admin — there are no "other" members to manage anyway.
  return input.clerkEnabled !== true;
}

export function canEditTeamMember(input: {
  readonly isSelf: boolean;
  readonly isAdmin: boolean;
}): boolean {
  return input.isSelf || input.isAdmin;
}

/** Whether the team page should wait on the DB-backed org directory fetch. */
export function isTeamOrgDirectoryLoading(input: {
  readonly organizationActive: boolean;
  readonly orgMembers: ReadonlyArray<unknown> | null | undefined;
}): boolean {
  return input.organizationActive && input.orgMembers === null;
}

/** Whether the team page should render from the org directory instead of local state. */
export function isTeamOrgDirectoryBacked(input: {
  readonly organizationActive: boolean;
  readonly orgMembers: ReadonlyArray<unknown> | null | undefined;
}): boolean {
  return input.organizationActive && input.orgMembers !== null;
}

/** Wait for Clerk roster data when the org directory view is active. */
export function isTeamClerkDirectoryLoading(input: {
  readonly organizationActive: boolean;
  readonly orgBacked: boolean;
  readonly clerkMembershipsLoading: boolean;
}): boolean {
  return input.organizationActive && input.orgBacked && input.clerkMembershipsLoading;
}
